import type { ReactElement } from 'react'
import { useId } from 'react'
import styles from './choice-group.module.css'

export interface ChoiceOption<T extends string> {
  value: T
  label: string
}

export interface ChoiceGroupProps<T extends string> {
  legend: string
  options: readonly ChoiceOption<T>[]
  value?: T
  className?: string
  onChange(value: T): void
}

/**
 * A radio group that looks like a segmented control: a `fieldset` named by its `legend`, native radios (so arrow keys
 * and screen readers work as for any radio group), each segment a label. Used for Who brings it and Monitoring.
 */
export const ChoiceGroup = <T extends string>(props: ChoiceGroupProps<T>): ReactElement => {
  const { legend, options, value, onChange, className } = props
  const name = useId()
  return (
    <fieldset className={[styles.group, className].filter(Boolean).join(' ')}>
      <legend className={styles.legend}>{legend}</legend>
      <div className={styles.segments}>
        {options.map(option => (
          <label key={option.value} className={styles.segment}>
            <input
              type="radio"
              className={styles.input}
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
            />
            <span className={styles.label}>{option.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}

export default ChoiceGroup
